import { getApiUrl } from '../config';
import { ChefResponseJob } from './conversationService';
import api from './apiService';
import { getEffectiveAuthToken, recoverFromAuthenticationFailure } from './authToken';

export interface ChefJobCallbacks {
  onSnapshot: (job: ChefResponseJob) => void;
  onDelta: (partialContent: string) => void;
  onComplete: (job: ChefResponseJob) => void;
  onCancelled: (job: ChefResponseJob) => void;
  onError: (error: Error, job?: ChefResponseJob) => void;
}

export class ChefStreamError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ChefStreamError';
    this.code = code;
  }
}

export const observeChefJob = (
  jobId: string,
  callbacks: ChefJobCallbacks
): () => void => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', getApiUrl(`ai/chat/jobs/${jobId}/stream`), true);
  const token = getEffectiveAuthToken();
  if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  xhr.responseType = 'text';

  let completed = false;
  let isAborted = false;
  let processedLength = 0;
  let leftover = '';

  const handleEvent = (eventName: string, data: any) => {
    if (eventName === 'snapshot') {
      callbacks.onSnapshot(data as ChefResponseJob);
      return;
    }
    if (eventName === 'delta' && typeof data.partialContent === 'string') {
      callbacks.onDelta(data.partialContent);
      return;
    }
    if (eventName === 'complete') {
      completed = true;
      callbacks.onComplete(data as ChefResponseJob);
      return;
    }
    if (eventName === 'cancelled') {
      completed = true;
      callbacks.onCancelled(data as ChefResponseJob);
      return;
    }
    if (eventName === 'error' || data.error) {
      completed = true;
      callbacks.onError(
        new ChefStreamError(data.error || 'Chef could not complete that request.', data.code),
        data.job as ChefResponseJob | undefined
      );
    }
  };

  xhr.onprogress = () => {
    const newData = xhr.responseText.substring(processedLength);
    if (!newData) return;
    processedLength = xhr.responseText.length;
    let dataToProcess = (leftover + newData).replace(/\r\n/g, '\n');
    let eventEndIndex: number;
    while ((eventEndIndex = dataToProcess.indexOf('\n\n')) !== -1) {
      const rawEvent = dataToProcess.slice(0, eventEndIndex);
      dataToProcess = dataToProcess.slice(eventEndIndex + 2);
      const lines = rawEvent.split('\n');
      const eventName = lines.find(line => line.startsWith('event:'))
        ?.replace(/^event:\s*/, '').trim() || 'message';
      const dataLine = lines.find(line => line.startsWith('data:'));
      if (!dataLine) continue;
      try {
        handleEvent(eventName, JSON.parse(dataLine.replace(/^data:\s*/, '').trim()));
      } catch (error) {
        console.debug('Could not parse Chef job event', error);
      }
    }
    leftover = dataToProcess;
  };

  xhr.onerror = () => {
    if (completed || isAborted) return;
    completed = true;
    callbacks.onError(new Error('Live updates were interrupted. Chef is still working.'));
  };

  xhr.onloadend = () => {
    if (completed || isAborted) return;
    if (xhr.status === 401) {
      recoverFromAuthenticationFailure();
      return;
    }
    completed = true;
    callbacks.onError(new Error('Live updates ended. Chef will continue in the background.'));
  };

  xhr.send();
  return () => {
    if (xhr.readyState !== 4) {
      isAborted = true;
      xhr.abort();
    }
  };
};

export const cancelChefJob = async (jobId: string): Promise<ChefResponseJob> => {
  const response = await api.post<ChefResponseJob>(getApiUrl(`ai/chat/jobs/${jobId}/cancel`));
  return response.data;
};

export const retryChefJob = async (jobId: string): Promise<ChefResponseJob> => {
  const response = await api.post<ChefResponseJob>(getApiUrl(`ai/chat/jobs/${jobId}/retry`));
  return response.data;
};
